import React, {useState, useEffect, useRef} from 'react';
import {Form, ListGroup, Spinner} from 'react-bootstrap';
import {useQuery, useQueryClient} from 'react-query';
import axios, {CancelTokenSource} from 'axios';
import Loading from "../../../common/components/Loading";
import {BuOption} from "../../../types/Customer";
import {useIntl} from 'react-intl';



interface AutocompleteBuProps {
    defaultValue: string;
    onChange: (value: BuOption) => void;
    label?: string;
    placeholder?: string;
    error?: string;
    touched?: boolean;
    name?: string;
    disabled?: boolean;
}

// Garder une référence à la dernière source de token d'annulation
let currentCancelTokenSource: CancelTokenSource | null = null;

const fetchBus = async (searchTerm: string): Promise<BuOption[]> => {
    if (!searchTerm) return [];

    // Annuler toute requête précédente
    if (currentCancelTokenSource) {
        currentCancelTokenSource.cancel('Requête annulée par une nouvelle recherche');
    }

    // Créer un nouveau token d'annulation
    currentCancelTokenSource = axios.CancelToken.source();

    try {
        const {data} = await axios.get('/api/users/bus', {
            params: {search: searchTerm},
            cancelToken: currentCancelTokenSource.token
        });
        return data;
    } catch (error) {
        if (axios.isCancel(error)) {
            // La requête a été annulée, on retourne un tableau vide
            return [];
        }
        throw error; // Propager les autres erreurs
    }
};

const AutocompleteBu = ({
                            defaultValue,
                            onChange,
                            label,
                            placeholder,
                            error,
                            touched,
                            name,
                            disabled = false
                        }: AutocompleteBuProps): JSX.Element => {
    const intl = useIntl();
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searchTerm, setSearchTerm] = useState(defaultValue);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(defaultValue);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [searchTerm]);

    useEffect(() => {
        return () => {
            if (currentCancelTokenSource) {
                currentCancelTokenSource.cancel('Composant démonté');
            }
        };
    }, []);


    const {
        data: options = [],
        isLoading,
        isFetching
    } = useQuery(
        ['bus', debouncedSearchTerm],
        () => fetchBus(debouncedSearchTerm),
        {
            enabled: debouncedSearchTerm.length > 0,
            staleTime: 60000,
            retry: false,
        }
    );

    useEffect(() => {
        if (defaultValue === searchTerm && options.length > 0) {
            setInputValue(formatOption(options[0]));
        }
    }, [defaultValue, options, searchTerm])

    const formatOption = (option: BuOption) => {
        return `${option.id} - ${option.name} (${option.system})`;
    };


    // Gestion du changement de saisie
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;
        setInputValue(text);
        setSearchTerm(text);
        setShowSuggestions(true);

        // Annuler explicitement la requête précédente via React Query
        if (currentCancelTokenSource) {
            currentCancelTokenSource.cancel('Nouvelle saisie');
            queryClient.cancelQueries(['bus', debouncedSearchTerm]);
        }
    };

    // Sélection d'une suggestion
    const handleSelectOption = (option: BuOption) => {
        setInputValue(formatOption(option));
        onChange(option);
        setShowSuggestions(false);
    };

    // Indique si un chargement est en cours
    const isSearchLoading = isLoading || isFetching;

    return (
        <div className="position-relative">
            {label && <Form.Label>{label}</Form.Label>}

            {inputValue === '' && isLoading ? (
                  <Loading />
                )
                : <>
                    <Form.Control
                        type="text"
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        isInvalid={touched && !!error}
                        name={name}
                        disabled={disabled}
                        autoComplete="off"
                    />

                    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}

                    {showSuggestions && (
                        <ListGroup
                            className="position-absolute w-100 mt-1 shadow-sm"
                            style={{zIndex: 1000, maxHeight: '300px', overflowY: 'auto'}}
                        >
                            {isSearchLoading ? (
                                <ListGroup.Item className="d-flex align-items-center justify-content-center py-3">
                                    <Spinner animation="border" size="sm" className="me-2"/>
                                    {intl.formatMessage({id: 'ADMIN.SEARCH.IN_PROGRESS'})}
                                </ListGroup.Item>
                            ) : options.length > 0 ? (
                                options.map(option => (
                                    <ListGroup.Item
                                        key={option.id}
                                        action
                                        onClick={() => handleSelectOption(option)}
                                        className="cursor-pointer"
                                    >
                                        {formatOption(option)}
                                    </ListGroup.Item>
                                ))
                            ) : debouncedSearchTerm ? (
                                <ListGroup.Item>
                                    {intl.formatMessage({id: 'ADMIN.SEARCH.NO_RESULT'})}
                                </ListGroup.Item>
                            ) : null}
                        </ListGroup>
                    )}
                </>}

        </div>
    );
};

export default AutocompleteBu;